import jsPDF from 'jspdf';
import {
    formatExerciseDisplay,
    formatExerciseValue,
    isCardioExercise,
    EXERCISE_TYPES,
} from '../../../utils/exerciseFormatters';
import { formatWorkoutRepsTarget } from '../../../constants/workoutRepsMode';
import {
    isPyramidExercise,
    ensureSeriesPlan,
    formatSeriesPlanSummary,
    formatSeriesPlanWeightSummary,
} from '../../../constants/workoutSeriesPlan';
import { pdfSafeText } from './pdfWorkoutText';

/** Accento oro del template tabellare (riferimento cliente). */
const ACCENT = [184, 149, 104];
const TEXT = [28, 28, 28];
const MUTED = [140, 140, 140];
const LINE = [220, 218, 214];
const INFO_BG = [247, 246, 244];
const BADGE_BG = [232, 214, 184];
const PAGE_MARGIN_X = 14;
const PAGE_MARGIN_TOP = 10;
const FOOTER_RESERVE = 14;
const HEADER_BLOCK_H = 22;
const INFO_BOX_H = 18;
const TABLE_HEADER_H = 8;
/** Logo un po' più grande (prima 14×12 mm), senza invadere la riga oro. */
const LOGO_MAX_W = 18;
const LOGO_MAX_H = 16;
const BRAND_LEFT_RESERVE = 52; // spazio logo + nome palestra a sinistra
const PAGE_LABEL_RESERVE = 28;
const HEADER_RULE_OFFSET = 18;
const HEADER_NAME_BASELINE = 10;
const HEADER_PAGE_BASELINE = 9;

/** Offset X relativi al margine sinistro (content ~182mm su A4). */
const COLS = {
    exercise: 0,
    serie: 56,
    rip: 72,
    recupero: 96,
    carico: 124,
    note: 150,
};

function isLinked(ex) {
    return ex?.linked_to_previous == 1
        || ex?.linked_to_previous === true
        || ex?.linked_to_previous === '1';
}

function parseExerciseNote(note) {
    if (!note) return '';
    if (typeof note === 'string' && note.trim().startsWith('{')) {
        try {
            const notesObj = JSON.parse(note);
            return notesObj.trainer || notesObj.user || notesObj.system || '';
        } catch (e) {
            return note.trim();
        }
    }
    if (typeof note === 'object' && note !== null) {
        return note.trainer || note.user || note.system || '';
    }
    return String(note).trim();
}

function formatRecoverySec(totalSeconds) {
    const n = parseInt(totalSeconds, 10);
    if (!Number.isFinite(n) || n <= 0) return '-';
    return `${n} sec`;
}

function formatDateIt(raw) {
    if (!raw) return '-';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
        const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[3]}/${m[2]}/${m[1]}`;
        return String(raw);
    }
    return d.toLocaleDateString('it-IT');
}

function formatWeightCell(esercizio) {
    if (esercizio.is_cardio === 1 || esercizio.is_cardio === true) {
        return '-';
    }
    if (isPyramidExercise(esercizio)) {
        const plan = ensureSeriesPlan(esercizio);
        const summary = formatSeriesPlanWeightSummary(plan, esercizio.peso, { pdf: true });
        if (!summary || summary === '0') return '-';
        return pdfSafeText(`${summary} kg`);
    }
    const peso = Number(esercizio.peso);
    if (!Number.isFinite(peso) || peso <= 0) return '-';
    const display = Number.isInteger(peso) ? String(peso) : String(peso).replace(/\.?0+$/, '');
    return `${display} kg`;
}

function formatRepsCell(esercizio) {
    if (isPyramidExercise(esercizio)) {
        const plan = ensureSeriesPlan(esercizio);
        return pdfSafeText(formatSeriesPlanSummary(plan, { pdf: true })) || '-';
    }
    // Cardio: il DB salva secondi, in stampa mostriamo minuti (come nel resto dell'app)
    if (isCardioExercise(esercizio)) {
        const seconds = Number(esercizio.ripetizioni);
        if (!Number.isFinite(seconds) || seconds <= 0) return '-';
        return pdfSafeText(`${formatExerciseValue(seconds, EXERCISE_TYPES.CARDIO)} min`);
    }
    const isIso = esercizio.is_isometric === 1
        || esercizio.is_isometric === true
        || esercizio.is_isometric_scheda === 1
        || esercizio.is_isometric_scheda === true;
    const target = formatWorkoutRepsTarget(esercizio);
    if (target) {
        if (isIso && /^\d+(\.\d+)?$/.test(String(target).trim())) {
            return pdfSafeText(`${target} sec`);
        }
        return pdfSafeText(target);
    }
    if (isIso) {
        const seconds = esercizio.ripetizioni != null ? String(esercizio.ripetizioni) : '';
        return seconds ? pdfSafeText(`${seconds} sec`) : '-';
    }
    const fallback = formatExerciseDisplay(esercizio.ripetizioni, esercizio);
    return pdfSafeText(fallback || '-');
}

function formatSerieCell(esercizio, { inCircuit = false } = {}) {
    if (inCircuit) return '-';
    const serie = parseInt(esercizio.serie, 10);
    return Number.isFinite(serie) && serie > 0 ? String(serie) : '-';
}

function formatRecoveryCell(
    esercizio,
    {
        inGroup = false,
        isLastInGroup = false,
        setType = 'normal',
        prefixLabel = '',
    } = {},
) {
    const rec = parseInt(esercizio.tempo_recupero, 10) || 0;
    if (setType === 'circuit') {
        if (isLastInGroup && rec > 0) {
            return pdfSafeText(`${rec} sec\nfine giro`);
        }
        return '-';
    }
    if (inGroup && !isLastInGroup) {
        return '-';
    }
    if (inGroup && isLastInGroup && rec > 0) {
        return pdfSafeText(`${rec} sec\ndopo ${prefixLabel}`.trim());
    }
    if (setType === 'dropset' && rec > 0) {
        return pdfSafeText(`${rec} sec\na fine serie`);
    }
    if (setType === 'piramidale' && rec > 0) {
        return formatRecoverySec(rec);
    }
    return rec > 0 ? formatRecoverySec(rec) : '-';
}

function italianCountWord(n) {
    const map = {
        2: 'due', 3: 'tre', 4: 'quattro', 5: 'cinque',
        6: 'sei', 7: 'sette', 8: 'otto', 9: 'nove', 10: 'dieci',
    };
    return map[n] || String(n);
}

function groupBannerCopy(setType, prefix, count, restSec, rounds = null) {
    const lastLabel = `${prefix}${count}`;
    const labels = Array.from({ length: count }, (_, i) => `${prefix}${i + 1}`);
    const restText = restSec > 0 ? `recupero ${restSec} sec dopo ${lastLabel}` : '';

    if (setType === 'superset') {
        return {
            badge: 'SUPERSET',
            left: pdfSafeText(`esegui ${labels.join(' + ')} senza recupero`),
            right: pdfSafeText(restText),
        };
    }
    if (setType === 'circuit') {
        const giroCount = rounds || count;
        return {
            badge: `CIRCUITO • ${giroCount} GIRI`,
            left: pdfSafeText(`completa ${labels[0]} -> ${labels[labels.length - 1]} in sequenza`),
            right: restSec > 0 ? pdfSafeText(`${restSec} sec a fine giro`) : '',
        };
    }
    if (setType === 'giant_set') {
        return {
            badge: 'GIANT SET',
            left: pdfSafeText(`${italianCountWord(count)} esercizi consecutivi dello stesso distretto`),
            right: pdfSafeText(restText),
        };
    }
    if (setType === 'dropset') {
        return {
            badge: 'DROP SET',
            left: pdfSafeText(`${count} cali di carico consecutivi`),
            right: pdfSafeText(restText),
        };
    }
    return {
        badge: (setType || 'GRUPPO').toUpperCase().replace('_', ' '),
        left: pdfSafeText(labels.join(' + ')),
        right: pdfSafeText(restText),
    };
}

function allocateGroupPrefix(setType, counters) {
    const defaults = { superset: 'A', circuit: 'C', giant_set: 'G', dropset: 'D' };
    const startChar = defaults[setType] || 'X';
    if (counters[setType] == null) {
        counters[setType] = startChar.charCodeAt(0);
    }
    const letter = String.fromCharCode(counters[setType]);
    counters[setType] += 1;
    return letter;
}

function buildBlocks(esercizi) {
    const blocks = [];
    const counters = {};
    let i = 0;
    while (i < esercizi.length) {
        const first = esercizi[i];
        if (isLinked(first)) {
            i += 1;
            continue;
        }
        const group = [first];
        let j = i + 1;
        while (j < esercizi.length && isLinked(esercizi[j])) {
            group.push(esercizi[j]);
            j += 1;
        }

        const setType = (first.set_type || 'normal').toLowerCase();
        if (group.length > 1 && setType !== 'normal' && setType !== 'piramidale') {
            const prefix = allocateGroupPrefix(setType, counters);
            const restSec = parseInt(group[group.length - 1].tempo_recupero, 10) || 0;
            const rounds = parseInt(first.serie, 10) || group.length;
            const banner = groupBannerCopy(setType, prefix, group.length, restSec, rounds);
            blocks.push({
                kind: 'group',
                setType,
                prefix,
                banner,
                exercises: group,
            });
        } else {
            group.forEach((ex) => {
                blocks.push({ kind: 'single', exercise: ex });
            });
        }
        i = j;
    }
    return blocks;
}

function contentBottom(pageHeight) {
    return pageHeight - FOOTER_RESERVE;
}

function contentWidth(pageWidth) {
    return pageWidth - PAGE_MARGIN_X * 2;
}

function drawFooter(doc, pageWidth, pageHeight, gymName) {
    const y = pageHeight - 8;
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.35);
    doc.line(PAGE_MARGIN_X, pageHeight - 11, pageWidth - PAGE_MARGIN_X, pageHeight - 11);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...ACCENT);
    const name = pdfSafeText(gymName || 'FitGym');
    doc.text(name, pageWidth - PAGE_MARGIN_X, y, { align: 'right' });
}

function fitLogoSize(naturalW, naturalH) {
    const aspect = (naturalW || 1) / (naturalH || 1);
    let logoWidth;
    let logoHeight;
    if (aspect > 1) {
        logoWidth = Math.min(LOGO_MAX_W, LOGO_MAX_H * aspect);
        logoHeight = logoWidth / aspect;
    } else {
        logoHeight = Math.min(LOGO_MAX_H, LOGO_MAX_W / aspect);
        logoWidth = logoHeight * aspect;
    }
    return { logoWidth, logoHeight };
}

function drawPageHeader(doc, athleteName, pageWidth, { gymName, logoImg } = {}) {
    const brandTop = PAGE_MARGIN_TOP;
    let brandRight = PAGE_MARGIN_X;

    // Logo + nome palestra in alto a sinistra (come template cliente)
    if (logoImg?.dataUrl && logoImg.width && logoImg.height) {
        const { logoWidth, logoHeight } = fitLogoSize(logoImg.width, logoImg.height);
        doc.addImage(
            logoImg.dataUrl,
            logoImg.format || 'PNG',
            PAGE_MARGIN_X,
            brandTop,
            logoWidth,
            logoHeight,
        );
        brandRight = PAGE_MARGIN_X + logoWidth + 2.5;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(...ACCENT);
        const brandLines = doc.splitTextToSize(
            pdfSafeText(gymName || 'FitGym'),
            BRAND_LEFT_RESERVE - logoWidth - 4,
        );
        const brandTextY = brandTop + Math.max(4, logoHeight / 2 + 1);
        doc.text(brandLines.slice(0, 2), brandRight, brandTextY);
        brandRight = PAGE_MARGIN_X + BRAND_LEFT_RESERVE;
    } else {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...ACCENT);
        const brandLines = doc.splitTextToSize(pdfSafeText(gymName || 'FitGym'), BRAND_LEFT_RESERVE - 2);
        doc.text(brandLines.slice(0, 2), PAGE_MARGIN_X, brandTop + 6);
        brandRight = PAGE_MARGIN_X + BRAND_LEFT_RESERVE;
    }

    // Nome atleta centrato, senza sovrapporsi a brand (sx) e paginazione (dx)
    const nameMaxW = Math.max(
        40,
        pageWidth - brandRight - PAGE_LABEL_RESERVE - PAGE_MARGIN_X - 6,
    );
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...TEXT);
    const nameLines = doc.splitTextToSize(
        pdfSafeText((athleteName || 'ATLETA').toUpperCase()),
        nameMaxW,
    );
    doc.text(nameLines[0], pageWidth / 2, PAGE_MARGIN_TOP + HEADER_NAME_BASELINE, { align: 'center' });

    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.4);
    doc.line(PAGE_MARGIN_X, PAGE_MARGIN_TOP + HEADER_RULE_OFFSET, pageWidth - PAGE_MARGIN_X, PAGE_MARGIN_TOP + HEADER_RULE_OFFSET);

    return PAGE_MARGIN_TOP + HEADER_BLOCK_H;
}

/** Carica logo palestra (PNG/JPG/SVG) con dimensioni. */
async function loadGymLogo(url) {
    if (!url || String(url).trim() === '') return null;

    const isSvg = url.toLowerCase().endsWith('.svg')
        || url.includes('.svg?')
        || url.includes('type=svg');

    if (isSvg) {
        try {
            const response = await fetch(url);
            const svgText = await response.text();
            const img = new Image();
            const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
            const svgUrl = URL.createObjectURL(svgBlob);
            const result = await new Promise((resolve, reject) => {
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth || img.width || 500;
                    canvas.height = img.naturalHeight || img.height || 500;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    URL.revokeObjectURL(svgUrl);
                    resolve({
                        dataUrl: canvas.toDataURL('image/png'),
                        width: canvas.width,
                        height: canvas.height,
                        format: 'PNG',
                    });
                };
                img.onerror = () => {
                    URL.revokeObjectURL(svgUrl);
                    reject(new Error('Errore caricamento SVG logo'));
                };
                img.src = svgUrl;
            });
            return result;
        } catch (e) {
            return null;
        }
    }

    try {
        return await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            const timeout = setTimeout(() => reject(new Error('Timeout logo')), 10000);
            img.onload = () => {
                clearTimeout(timeout);
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const isJpg = url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg');
                resolve({
                    dataUrl: canvas.toDataURL(isJpg ? 'image/jpeg' : 'image/png'),
                    width: img.width,
                    height: img.height,
                    format: isJpg ? 'JPEG' : 'PNG',
                });
            };
            img.onerror = () => {
                clearTimeout(timeout);
                reject(new Error('Errore caricamento logo'));
            };
            img.src = url;
        });
    } catch (e) {
        return null;
    }
}

function drawPageLabel(doc, pageWidth, page, totalPages) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
        pdfSafeText(`PAG. ${page}/${totalPages}`),
        pageWidth - PAGE_MARGIN_X,
        PAGE_MARGIN_TOP + HEADER_PAGE_BASELINE,
        { align: 'right' },
    );
}

function drawInfoBox(doc, y, pageWidth, { schedaName, obiettivo, inizio, continuation }) {
    const width = contentWidth(pageWidth);
    const h = continuation ? 12 : INFO_BOX_H;
    doc.setFillColor(...INFO_BG);
    doc.roundedRect(PAGE_MARGIN_X, y, width, h, 2, 2, 'F');

    if (continuation) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor(...TEXT);
        doc.text('CONTINUAZIONE ALLENAMENTO', PAGE_MARGIN_X + 5, y + 7.5);
        return y + h + 4;
    }

    const col2 = PAGE_MARGIN_X + width * 0.48;
    const col3 = PAGE_MARGIN_X + width * 0.74;
    const titleMaxW = col2 - PAGE_MARGIN_X - 10;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...TEXT);
    const titleLines = doc.splitTextToSize(pdfSafeText(schedaName || 'Scheda'), titleMaxW);
    doc.text(titleLines.slice(0, 2), PAGE_MARGIN_X + 5, y + (titleLines.length > 1 ? 7 : 11));

    doc.setDrawColor(210, 208, 204);
    doc.setLineWidth(0.2);
    doc.line(col2 - 4, y + 3, col2 - 4, y + h - 3);
    doc.line(col3 - 4, y + 3, col3 - 4, y + h - 3);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...MUTED);
    doc.text('OBIETTIVO', col2, y + 6);
    doc.text('INIZIO', col3, y + 6);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...TEXT);
    const obiettivoLines = doc.splitTextToSize(pdfSafeText(obiettivo || '-'), width * 0.24);
    doc.text(obiettivoLines.slice(0, 2), col2, y + 11);
    doc.text(pdfSafeText(inizio || '-'), col3, y + 11);

    return y + h + 5;
}

function drawTableHeader(doc, y, pageWidth) {
    const width = contentWidth(pageWidth);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    const baseX = PAGE_MARGIN_X;
    doc.text('ESERCIZIO', baseX + COLS.exercise, y);
    doc.text('SERIE', baseX + COLS.serie + 4, y, { align: 'center' });
    doc.text('RIP.', baseX + COLS.rip + 6, y, { align: 'center' });
    doc.text('RECUPERO', baseX + COLS.recupero + 8, y, { align: 'center' });
    doc.text('CARICO', baseX + COLS.carico + 8, y, { align: 'center' });
    doc.text('NOTE DAL PT', baseX + COLS.note, y);

    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(baseX, y + 2.5, baseX + width, y + 2.5);
    return y + TABLE_HEADER_H;
}

function noteColumnWidth(pageWidth) {
    return contentWidth(pageWidth) - COLS.note - 2;
}

function measureExerciseRow(doc, esercizio, pageWidth, options = {}) {
    const note = parseExerciseNote(esercizio.note || esercizio.notes);
    doc.setFontSize(7.5);
    const noteLines = note
        ? doc.splitTextToSize(pdfSafeText(note), noteColumnWidth(pageWidth))
        : [];
    const name = pdfSafeText(esercizio.nome || 'Esercizio');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const nameMaxW = options.prefixLabel || options.prefix
        ? COLS.serie - 14
        : COLS.serie - 4;
    const nameLines = doc.splitTextToSize(name, nameMaxW);

    // Badge + sottotitolo sotto il nome (mai affiancati al titolo)
    let extraLines = 0;
    const setType = (esercizio.set_type || 'normal').toLowerCase();
    if (!options.inGroup && (setType === 'dropset' || setType === 'piramidale')) {
        extraLines = 2; // badge + riga descrittiva
    }

    const recovery = formatRecoveryCell(esercizio, options);
    const recoveryLines = String(recovery).split('\n').length;

    const contentLines = Math.max(nameLines.length + extraLines, noteLines.length, recoveryLines, 1);
    return Math.max(10, 4 + contentLines * 3.4 + (options.inGroup ? 1 : 2));
}

function measureBanner(setType) {
    return setType === 'circuit' || setType === 'superset' || setType === 'giant_set' ? 8 : 7;
}

function measureBlock(doc, block, pageWidth) {
    if (block.kind === 'single') {
        return measureExerciseRow(doc, block.exercise, pageWidth);
    }
    let h = measureBanner(block.setType) + 2;
    block.exercises.forEach((ex, idx) => {
        h += measureExerciseRow(doc, ex, pageWidth, {
            inGroup: true,
            inCircuit: block.setType === 'circuit',
            isLastInGroup: idx === block.exercises.length - 1,
            setType: block.setType,
            prefix: block.prefix,
        });
    });
    return h + 2;
}

function drawBadge(doc, x, y, label) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6);
    const text = pdfSafeText(label);
    const w = doc.getTextWidth(text) + 4;
    const h = 3.8;
    doc.setFillColor(...ACCENT);
    doc.roundedRect(x, y - 2.7, w, h, 0.8, 0.8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(text, x + 2, y);
    return w;
}

function drawExerciseRow(doc, y, pageWidth, esercizio, options = {}) {
    const rowH = measureExerciseRow(doc, esercizio, pageWidth, options);
    const baseX = PAGE_MARGIN_X;
    const width = contentWidth(pageWidth);
    const setType = (esercizio.set_type || 'normal').toLowerCase();

    if (options.inGroup) {
        doc.setFillColor(...ACCENT);
        doc.rect(baseX, y, 1.1, rowH, 'F');
    }

    let nameX = baseX + 2;
    if (options.prefixLabel) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...ACCENT);
        doc.text(options.prefixLabel, nameX + 2, y + 4.5);
        nameX += 9;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...TEXT);
    const nameMaxW = COLS.serie - (nameX - baseX) - 2;
    const nameLines = doc.splitTextToSize(pdfSafeText(esercizio.nome || 'Esercizio'), nameMaxW);
    doc.text(nameLines, nameX, y + 4.5);

    let cursorY = y + 4.5 + (nameLines.length - 1) * 3.4;
    if (!options.inGroup && setType === 'dropset') {
        cursorY += 4.2;
        drawBadge(doc, nameX, cursorY, 'DROP SET');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...MUTED);
        const drops = parseInt(esercizio.serie, 10) || 3;
        doc.text(pdfSafeText(`${drops} cali di carico consecutivi`), nameX, cursorY + 3.8);
        cursorY += 3.8;
    } else if (!options.inGroup && setType === 'piramidale') {
        cursorY += 4.2;
        drawBadge(doc, nameX, cursorY, 'PIRAMIDALE');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6.5);
        doc.setTextColor(...MUTED);
        doc.text('carico crescente / ripetizioni decrescenti', nameX, cursorY + 3.8);
        cursorY += 3.8;
    }

    const centerY = y + Math.min(5.5, rowH / 2 + 1);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...TEXT);

    doc.text(
        formatSerieCell(esercizio, { inCircuit: options.inCircuit }),
        baseX + COLS.serie + 4,
        centerY,
        { align: 'center' },
    );
    doc.text(formatRepsCell(esercizio), baseX + COLS.rip + 6, centerY, { align: 'center' });

    const recovery = formatRecoveryCell(esercizio, options);
    const recoveryLines = String(recovery).split('\n');
    doc.setFontSize(7.5);
    recoveryLines.forEach((line, idx) => {
        doc.text(line, baseX + COLS.recupero + 8, centerY + idx * 3, { align: 'center' });
    });

    doc.setFontSize(8);
    const caricoLines = doc.splitTextToSize(formatWeightCell(esercizio), 26);
    doc.text(caricoLines, baseX + COLS.carico + 8, centerY, { align: 'center' });

    const note = parseExerciseNote(esercizio.note || esercizio.notes);
    if (note) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(70, 70, 70);
        const noteLines = doc.splitTextToSize(pdfSafeText(note), noteColumnWidth(pageWidth));
        doc.text(noteLines, baseX + COLS.note, y + 4.5);
    }

    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.25);
    doc.line(baseX, y + rowH - 0.5, baseX + width, y + rowH - 0.5);

    return y + rowH;
}

function drawGroupBanner(doc, y, pageWidth, banner) {
    const width = contentWidth(pageWidth);
    const h = 7.5;
    const baseX = PAGE_MARGIN_X;

    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(baseX, y, baseX + width, y);

    const badgeW = drawBadge(doc, baseX + 2, y + 4.8, banner.badge);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...TEXT);
    doc.text(banner.left || '', baseX + 2 + badgeW + 3, y + 4.8);

    if (banner.right) {
        doc.setTextColor(...MUTED);
        doc.text(banner.right, baseX + width - 2, y + 4.8, { align: 'right' });
    }

    doc.setDrawColor(...LINE);
    doc.line(baseX, y + h, baseX + width, y + h);
    return y + h + 1;
}

/**
 * Template tabellare stampabile (senza immagini esercizi), terzo stile PDF.
 * Header: logo + nome palestra a sinistra, atleta al centro, paginazione a destra.
 */
export const generateWorkoutPDFTable = async (
    workout,
    athlete,
    gymLogoUrl,
    gymName = 'FitGym',
    _apiBaseUrl = null,
) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const athleteName = athlete?.name || athlete?.nome || 'Atleta';
    const schedaName = workout?.nome || 'Scheda';
    // Campo "Obiettivo" in create/edit → colonna schede.descrizione
    const obiettivo = workout?.obiettivo || workout?.descrizione || '';
    const inizio = formatDateIt(workout?.assigned_at || workout?.assigned_date || workout?.data_assegnazione);
    const blocks = buildBlocks(workout?.esercizi || []);

    let logoImg = null;
    if (gymLogoUrl) {
        logoImg = await loadGymLogo(gymLogoUrl);
    }

    const startNewPage = (isFirst) => {
        if (!isFirst) {
            doc.addPage();
        }
        let y = drawPageHeader(doc, athleteName, pageWidth, { gymName, logoImg });
        y = drawInfoBox(doc, y, pageWidth, {
            schedaName,
            obiettivo,
            inizio,
            continuation: !isFirst,
        });
        y = drawTableHeader(doc, y, pageWidth);
        return y;
    };

    let y = startNewPage(true);
    const bottom = () => contentBottom(pageHeight);

    for (const block of blocks) {
        const needed = measureBlock(doc, block, pageWidth);
        if (y + needed > bottom()) {
            y = startNewPage(false);
        }

        if (block.kind === 'group') {
            // Keep banner + first exercise together when possible
            const bannerH = measureBanner(block.setType) + 2;
            const firstRowH = measureExerciseRow(doc, block.exercises[0], pageWidth, {
                inGroup: true,
                inCircuit: block.setType === 'circuit',
                isLastInGroup: block.exercises.length === 1,
                setType: block.setType,
                prefix: block.prefix,
            });
            if (y + bannerH + firstRowH > bottom()) {
                y = startNewPage(false);
            }

            y = drawGroupBanner(doc, y, pageWidth, block.banner);

            block.exercises.forEach((ex, idx) => {
                const opts = {
                    inGroup: true,
                    inCircuit: block.setType === 'circuit',
                    isLastInGroup: idx === block.exercises.length - 1,
                    setType: block.setType,
                    prefix: block.prefix,
                    prefixLabel: `${block.prefix}${idx + 1}`,
                };
                const rowH = measureExerciseRow(doc, ex, pageWidth, opts);
                if (y + rowH > bottom()) {
                    y = startNewPage(false);
                }
                y = drawExerciseRow(doc, y, pageWidth, ex, opts);
            });
            y += 2;
        } else {
            y = drawExerciseRow(doc, y, pageWidth, block.exercise);
            y += 1;
        }
    }

    const totalPages = doc.getNumberOfPages();
    for (let p = 1; p <= totalPages; p += 1) {
        doc.setPage(p);
        drawPageLabel(doc, pageWidth, p, totalPages);
        drawFooter(doc, pageWidth, pageHeight, gymName);
    }

    return doc;
};

export default generateWorkoutPDFTable;
